import React from 'react';

export interface RaporEventRacketCardProps {
  playerName: string;
  eventName?: string;
  eventDateFormatted?: string;
  location?: string;
  sportCategory?: string;
  rank?: number;
  matches: number;
  winRate: number;
  point: number;
  pointLoss: number;
  bgImage?: string;
  transparent?: boolean;
  className?: string;
}

const DEFAULT_BG_IMAGE = 'https://cdn.sporto.id/bgshare.webp';
const SPORTO_LOGO_URL = 'https://cdn.sporto.id/sporto.svg';

export const RaporEventRacketCard: React.FC<RaporEventRacketCardProps> = ({
  playerName,
  rank = 1,
  matches,
  winRate,
  point = 0,
  pointLoss = 0,
  bgImage,
  transparent = true,
  className = '',
}) => {
  const activeBgImage = bgImage || DEFAULT_BG_IMAGE;
  const totalPoints = point + pointLoss;
  const pointPercentage = totalPoints > 0 ? Math.min(100, Math.max(0, Math.round((point / totalPoints) * 100))) : 50;

  return (
    <div
      className={`relative w-[540px] h-[960px] overflow-hidden select-none ${
        transparent ? 'bg-transparent' : 'bg-[#0E1726] shadow-[0_8px_30px_rgba(14,23,38,0.18)]'
      } ${className}`}
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      {/* Background Image & Gradient (only if not transparent) */}
      {!transparent && (
        <>
          <img
            src={activeBgImage}
            alt="Foto pertandingan padel"
            className="absolute inset-0 w-full h-full object-cover object-[58%_34%]"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg,rgba(14,23,38,.25) 0%,rgba(14,23,38,.05) 34%,rgba(0,0,0,.55) 62%,rgba(0,0,0,.92) 100%)',
            }}
          />
        </>
      )}

      {/* Shadow Gradient Overlay (provides transparent contrast for text over InstaStory photo) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(180deg, rgba(14, 23, 38, 0.35) 0%, rgba(14, 23, 38, 0.15) 32%, rgba(14, 23, 38, 0.55) 62%, rgba(14, 23, 38, 0.95) 100%)',
        }}
      />

      {/* Content Container */}
      <div className="absolute inset-0 flex flex-col justify-end p-[26px_24px_24px]">
        {/* Dark Glassmorphic Box */}
        <div
          className="relative rounded-[20px] p-[28px_26px_26px] overflow-hidden"
          style={{
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'linear-gradient(180deg, rgba(20,20,22,0.62) 0%, rgba(12,12,14,0.86) 100%)',
            backdropFilter: 'blur(12px)',
          }}
        >
          {/* Big Rank Background Watermark */}
          <div
            className="absolute top-[-14px] right-[14px] font-extrabold text-[132px] leading-[132px] tracking-[-0.04em] pointer-events-none select-none"
            style={{ color: 'rgba(255,255,255,0.055)' }}
          >
            #{rank}
          </div>

          <div className="relative flex flex-col gap-[22px]">
            {/* Top Row: Player Name + Rank Pill & Win Rate */}
            <div className="flex items-center justify-between gap-[16px]">
              <div className="flex items-center gap-[10px] min-w-0 flex-1">
                <div className="text-[28px] font-extrabold text-white leading-[34px] tracking-[-0.02em] uppercase truncate">
                  {playerName}
                </div>
                <div className="bg-[#FC480D] rounded-[8px] px-[10px] py-[4px] text-[15px] font-extrabold text-white leading-[18px] italic shrink-0">
                  #{rank}
                </div>
              </div>
              <div className="flex flex-col items-end gap-[1px] shrink-0">
                <div
                  className="text-[11px] font-semibold tracking-[0.12em] text-white/50 uppercase"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  WIN RATE
                </div>
                <div className="text-[34px] font-extrabold text-[#FC480D] leading-[36px] tracking-[-0.03em]">
                  {Math.round(winRate)}%
                </div>
              </div>
            </div>

            {/* Middle Box: Total Match & Point Breakdown */}
            <div
              className="rounded-[16px] p-[20px_22px] flex flex-col gap-[16px]"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              {/* Total Match Row */}
              <div className="flex items-center justify-between gap-[16px]">
                <div
                  className="text-[14px] font-semibold tracking-[0.12em] text-white/60 uppercase"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  TOTAL MATCH
                </div>
                <div className="text-[34px] font-extrabold text-white leading-[36px] tracking-[-0.02em]">
                  {matches}
                </div>
              </div>

              {/* Divider */}
              <div className="h-[2px] bg-white/20" />

              {/* Point vs Point Loss Row */}
              <div className="flex flex-col gap-[10px]">
                <div className="flex items-baseline justify-between gap-[16px]">
                  <div className="flex items-baseline gap-[7px]">
                    <span className="text-[14px] font-semibold tracking-[0.08em] text-[#FC480D]">
                      POINT
                    </span>
                    <span className="text-[17px] font-extrabold text-[#FC480D]">{point}</span>
                  </div>
                  <div className="flex items-baseline gap-[7px]">
                    <span className="text-[14px] font-semibold tracking-[0.08em] text-white/55">
                      POINT LOSS
                    </span>
                    <span className="text-[17px] font-extrabold text-white/75">{pointLoss}</span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="h-[8px] rounded-full bg-white/14 overflow-hidden flex">
                  <div
                    className="h-full bg-[#FC480D] rounded-full transition-all duration-300"
                    style={{ width: `${pointPercentage}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Logo */}
        <div className="flex justify-center pt-[22px]">
          <img src={SPORTO_LOGO_URL} alt="Sporto" className="h-[26px] w-auto object-contain block" />
        </div>
      </div>
    </div>
  );
};
